import { Injectable } from '@angular/core';
import { FormGroup, FormControl, Validator, Validators, ValidatorFn, ValidationErrors, FormGroupDirective, NgForm } from '@angular/forms';
import { Http, Response } from "@angular/http";
import { Headers, RequestOptions } from '@angular/http';
import { Candidate } from 'src/app/model/candidate.model';
import { ApiService } from './api.service';
import * as _ from 'lodash';
import { ErrorStateMatcher } from '@angular/material';

/** Error when the parent is invalid */
export class CrossFieldErrorMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Injectable({
  providedIn: 'root'
})
export class CandidateService {
  errorMatcher = new CrossFieldErrorMatcher();

  constructor(private http: Http,
    private apiService: ApiService) { }

  private apiDomain = ApiService.getDomainName();
  private eventId = this.apiService.getEventId();

  validateScore: ValidatorFn = (form: FormGroup): ValidationErrors | null => {
    const invalidScore = form.get('score').value > form.get('totalMarks').value
    return invalidScore ? { 'scoreValidator': true } : null;
  };


  form: FormGroup = new FormGroup({
    id: new FormControl(null),
    candidateName: new FormControl('', [Validators.required, Validators.minLength(3), Validators.pattern(("[a-zA-Z][a-zA-Z ]+"))]),
    email: new FormControl('', [Validators.required, Validators.email]),
    contactNo: new FormControl('', [Validators.required, Validators.pattern(("[1-9]\\d{9}"))]),
    address: new FormControl('', Validators.required),
    dob: new FormControl('', Validators.required),
    gender: new FormControl('', Validators.required),
    createdId: new FormControl(''),
    totalMarks: new FormControl(''),
    score: new FormControl(''),
    reportURL: new FormControl(''),
    attemptStatus: new FormControl('', Validators.required),
    status: new FormControl(''),
    parentId: new FormControl(''),
    panelAssigned: new FormControl('')
  }, { validators: this.validateScore });


  private handleError(error: any) {
    console.error('An error occurred', error);
    try {
       const errorJSON = error && error.json();
      if ( errorJSON.httpCode == 401 ) {
        ApiService.sessionExpireRedirection();
      }
      return Promise.reject(errorJSON);
    } catch(err) {
      return Promise.reject(error && error._body && error.message);
    }
  }

  loadCandidatesFromParentSystem() {
    const headers = new Headers({ 'Content-Type': 'application/json', 'authorization': this.apiService.getStringifySessionObj() });
    return this.http.get(`${this.apiDomain}/ims/api/poolCandidatesFromParentSystem`, { headers })
      .toPromise()
      .then(response => (<any>response)._body)
      .catch(this.handleError);
  }

  findAll(): Promise<Array<Candidate>> {
    const headers = new Headers({ 'Content-Type': 'application/json', 'authorization': this.apiService.getStringifySessionObj() })

    return this.http.get(this.apiDomain + "/ims/api/" + this.eventId + "/candidate/", { headers })
      .toPromise()
      .then(response => response.json() as Candidate[])
      .catch(this.handleError);
  }

  findById(candidateId) {
    const headers = new Headers({ 'Content-Type': 'application/json', 'authorization': this.apiService.getStringifySessionObj() });
    return this.http.get(this.apiDomain + "/ims/api/candidate/" + candidateId, { headers })
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }

  createCandidate(candidate: Candidate[]): any {
    let candidateHeaders = new Headers({ 'Content-Type': 'application/json', 'authorization': this.apiService.getStringifySessionObj() });
    let dataObj = JSON.stringify(candidate);
    return this.http.post(this.apiDomain + "/ims/api/v2/" + this.eventId + "/candidate/", dataObj, { headers: candidateHeaders })
      .toPromise()
      .then(response => response.json() as any)
      .catch(this.handleError);
  }

  updateCandidate(candidate: Candidate, candidateId): Promise<Array<Candidate>> {
    let panelHeaders = new Headers({ 'Content-Type': 'application/json', 'authorization': this.apiService.getStringifySessionObj() });
    return this.http.put(this.apiDomain + "/ims/api/candidate/" + candidateId, JSON.stringify(candidate), { headers: panelHeaders })
      .toPromise()
      .then(response => response.json() as Candidate[])
      .catch(this.handleError);
  }

  deleteCandidate(id) {
    const headers = new Headers({ 'Content-Type': 'application/json', 'authorization': this.apiService.getStringifySessionObj() })
    const url = this.apiDomain + "/ims/api/candidate/" + id
    return this.http.delete(url, { headers })
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }

  uploadCandidate(file) {
    let candidateHeaders = new Headers({ 'Content-Type': 'text/plain', "file": file, 'authorization': this.apiService.getStringifySessionObj() });
    return this.http.post(this.apiDomain + "ims/api/" + this.eventId + "/candidate/upload/", { headers: candidateHeaders })
      .toPromise()
      .then(response => response.json() as Candidate[])
      .catch(this.handleError);
  }
}
